/* eslint-disable react-refresh/only-export-components */
import { createContext, useContext, useState, ReactNode } from "react";
import type { Property } from "@/hooks/useProperties";

interface ComparisonContextType {
  selectedProperties: Property[];
  addProperty: (property: Property) => void;
  removeProperty: (propertyId: number) => void;
  clearSelection: () => void;
  isSelected: (propertyId: number) => boolean;
}

const ComparisonContext = createContext<ComparisonContextType | undefined>(undefined);

export const ComparisonProvider = ({ children }: { children: ReactNode }) => {
  const [selectedProperties, setSelectedProperties] = useState<Property[]>([]);

  const addProperty = (property: Property) => {
    if (selectedProperties.length < 4 && !selectedProperties.find(p => p.id === property.id)) {
      setSelectedProperties([...selectedProperties, property]);
    }
  };

  const removeProperty = (propertyId: number) => {
    setSelectedProperties(selectedProperties.filter(p => p.id !== propertyId));
  };

  const clearSelection = () => {
    setSelectedProperties([]);
  };

  const isSelected = (propertyId: number) => {
    return selectedProperties.some(p => p.id === propertyId);
  };

  return (
    <ComparisonContext.Provider
      value={{
        selectedProperties,
        addProperty,
        removeProperty,
        clearSelection,
        isSelected,
      }}
    >
      {children}
    </ComparisonContext.Provider>
  );
};

export const useComparison = () => {
  const context = useContext(ComparisonContext);
  if (context === undefined) {
    throw new Error("useComparison must be used within a ComparisonProvider");
  }
  return context;
};
